import * as monthUtils from '@actual-app/core/shared/months';

/**
 * Acha o primeiro mês, a partir de `mesCorrente` (inclusive), em que `dia`
 * é um dia de verdade — e devolve a data completa (`YYYY-MM-DD`) nesse mês.
 *
 * ⚠️⚠️ FIX ROUND 1 (Important 1) — existe pra `FormularioCompromisso` nunca
 * montar `start` direto com `${mes}-${dia}` quando `dia` é 29/30/31 e o mês
 * corrente não tem esse dia. `monthUtils.parseDate` usa
 * `new Date(year, month-1, day, 12)`, e o construtor nativo do JS ROLA o
 * excesso pro mês seguinte: `new Date(2026, 8, 31, 12)` (31 de setembro,
 * que não existe) vira 1º de outubro — silenciosamente, sem lançar erro.
 * Como o formulário manda `patterns: []`, a recorrência mensal do agendado
 * se ancora nesse dia JÁ ROLADO: "aluguel todo dia 31" virava, de verdade,
 * "aluguel todo dia 1" — sem aviso nenhum na tela.
 *
 * A correção não é "grudar" o dia no mês corrente de qualquer jeito — é
 * andar pra frente até achar um mês onde aquele dia EXISTE de verdade, e
 * ancorar ali. Dia 31 em setembro (30 dias) → outubro (31 dias). Dia 30 em
 * fevereiro → março. Isso nunca cria uma data inválida nem deixa o
 * `Date` nativo decidir por conta própria.
 *
 * ⚠️ Verificado contra o motor de recorrência de verdade
 * (`recurConfigToRSchedule` em `packages/loot-core/src/shared/schedules.ts`)
 * antes de escrever isto: NÃO existe um `pattern` nativo que signifique
 * "todo dia 31, e nos meses sem dia 31 usa o último dia" para um dia
 * arbitrário. O único jeito nativo de dizer "sempre o último dia do mês" é
 * `{ type: 'day', value: -1 }` — mas isso SEMPRE usa o último dia, mesmo em
 * meses de 31 dias (então um dia 30 digitado viraria dia 31 em janeiro, o
 * que o dono não pediu). `-1` só coincide com "dia 31, ou o último dia
 * quando não existe" quando o dia digitado É 31 — não serve para 29/30 em
 * geral. Por isso a correção mora aqui (uma função pura e testada), não
 * numa troca de `pattern`. Ver o relatório da Task 7 para a demonstração
 * (uma consulta real de ocorrências mostrando os dois comportamentos).
 *
 * ⚠️ O laço tem um teto (`MAX_TENTATIVAS`) só como cinto de segurança: para
 * qualquer `dia` entre 1 e 31, o próximo mês com 31 dias nunca está a mais
 * de 1 mês de distância (fevereiro é o único mês "curto" que pode faltar
 * dia 29/30/31, e o mês seguinte a fevereiro sempre tem pelo menos 30
 * dias). Nunca deveria estourar o teto com uma entrada válida — se estourar,
 * é sinal de um `dia` fora de 1–31, que é responsabilidade de quem chama
 * validar antes (ver `FormularioCompromisso.salvar`).
 */
export function primeiroVencimento(dia: number, mesCorrente: string): string {
  const MAX_TENTATIVAS = 4;
  let mes = mesCorrente;

  for (let tentativa = 0; tentativa < MAX_TENTATIVAS; tentativa++) {
    const ultimoDiaDoMes = Number(monthUtils.lastDayOfMonth(mes).slice(-2));
    if (dia <= ultimoDiaDoMes) {
      return `${mes}-${String(dia).padStart(2, '0')}`;
    }
    mes = monthUtils.nextMonth(mes);
  }

  // Não deveria chegar aqui para um `dia` válido (1–31) — ver o JSDoc acima.
  return `${mesCorrente}-${String(dia).padStart(2, '0')}`;
}

const NOMES_MES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * Revisão final da branch (Finding 3, Important) — quais meses NÃO têm o
 * `dia` informado, pra `FormularioCompromisso` avisar mesmo quando a
 * PRIMEIRA ocorrência cai certinha no mês corrente.
 *
 * `primeiroVencimento` (acima) só resolve o problema da PRIMEIRA ocorrência
 * — depois disso, a recorrência mensal nativa do Actual (`patterns: []`)
 * PULA silenciosamente os meses em que aquele dia não existe (RFC5545):
 * "todo dia 31" nunca ocorre em fevereiro, abril, junho, setembro e
 * novembro. Isso é comportamento nativo do motor de recorrência — idêntico
 * ao da tela `/schedules` do Actual — e esta função não tenta corrigi-lo
 * (⛔ mexer no motor de recorrência está fora de escopo); ela só torna o
 * comportamento VISÍVEL antes do dono cadastrar.
 *
 * ⚠️ Calculada sobre um ano de referência NÃO bissexto (2025, fixo): para o
 * dia 29, fevereiro aparece aqui como "sem esse dia" mesmo em anos
 * bissextos que o têm. É o lado conservador do aviso — avisa numa
 * fração de anos em que não precisaria, nunca deixa de avisar num ano em
 * que precisaria.
 */
export function mesesSemODia(dia: number): string[] {
  const nomes: string[] = [];
  for (let mes = 1; mes <= 12; mes++) {
    const chave = `2025-${String(mes).padStart(2, '0')}`;
    const ultimoDiaDoMes = Number(monthUtils.lastDayOfMonth(chave).slice(-2));
    if (dia > ultimoDiaDoMes) nomes.push(NOMES_MES[mes - 1]);
  }
  return nomes;
}
